import React, { useState, useMemo, useCallback, memo } from "react";
import { FaTimes } from "react-icons/fa";
import { normalizeItemSearchText } from "../../../utils/itemSearchText";

const AddItemModal = ({
  isOpen,
  onClose,
  inventory,
  items,
  onAddItem,
  onRemoveFromCart,
  onCartQuantityChange,
  onClearCart,
  cart,
  getRemainingQuantity,
  isChildUser = false,
  childUserPersonalNumber = null,
  availableItemNames: propAvailableItemNames = null,
  selectedEmployee = null,
  /** ({ serialNumber, itemDefinitionId }) → navigate to Inventory add-stock shortcut */
  onAddStockShortcut = null,
}) => {
  const [itemSearchTerm, setItemSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemOptions, setShowItemOptions] = useState(false);
  const [serialSearchTerm, setSerialSearchTerm] = useState("");
  const [selectedSerial, setSelectedSerial] = useState(null);
  const [showSerialOptions, setShowSerialOptions] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState("");
  const [showItemOptionsAbove, setShowItemOptionsAbove] = useState(false);
  const [showSerialOptionsAbove, setShowSerialOptionsAbove] = useState(false);

  // Get unique item names from inventory (use prop if provided, otherwise calculate)
  const availableItemNames = useMemo(() => {
    // If provided via props (filtered in parent), use it
    if (propAvailableItemNames && propAvailableItemNames.length > 0) {
      return propAvailableItemNames.map((item) => ({
        name: item.isChecked
          ? item.itemName
          : `${item.itemName} (${item.isOrganic ? "אורגני" : "קווי"})`,
        availableQuantity: item.availableQuantity,
      }));
    }

    // Otherwise calculate (for admin/parentsUser)
    if (!inventory?.length || !items?.length) {
      return [];
    }

    const itemMap = new Map();
    inventory.forEach((invItem) => {
      const itemDefinition = items.find(
        (item) => item.id === invItem.itemDefinitionId
      );
      if (itemDefinition) {
        // For serial items, use the item name as is
        // For non-serial items, distinguish between organic and synthetic
        const key = itemDefinition.isChecked
          ? itemDefinition.itemName
          : `${itemDefinition.itemName} (${
              invItem.isOrganic ? "אורגני" : "קווי"
            })`;

        // For admin/parentsUser: use availableQuantity from inventory collection for non-serial items
        // For serial items: count items with status === "available"
        let quantityToAdd = 0;
        if (invItem.serialNumber) {
          // Serial item: count if available
          if (invItem.status === "available") {
            quantityToAdd = 1;
          }
        } else {
          // Non-serial item: use availableQuantity field from inventory collection
          quantityToAdd = invItem.availableQuantity ?? 0;
        }

        const existing = itemMap.get(key) || 0;
        itemMap.set(key, existing + quantityToAdd);
      }
    });

    return Array.from(itemMap.entries()).map(([name, availableQuantity]) => ({
      name,
      availableQuantity,
    }));
  }, [inventory, items, propAvailableItemNames]);

  // Filter items based on search term
  const filteredItems = useMemo(() => {
    if (!itemSearchTerm.trim()) return availableItemNames;

    return availableItemNames.filter((item) =>
      normalizeItemSearchText(item.name).includes(
        normalizeItemSearchText(itemSearchTerm)
      )
    );
  }, [availableItemNames, itemSearchTerm]);

  // Get available serials for selected item (filtered for childUser)
  const availableSerials = useMemo(() => {
    if (!selectedItem || !inventory?.length) return [];

    // Extract base item name for serial lookup
    let itemName = selectedItem;
    if (selectedItem.includes(" (") && selectedItem.includes(")")) {
      const match = selectedItem.match(/^(.+) \((אורגני|קווי)\)$/);
      if (match) {
        itemName = match[1];
      }
    }

    const itemDefinition = items.find((item) => item.itemName === itemName);
    if (!itemDefinition || !itemDefinition.isChecked) return [];

    if (isChildUser && childUserPersonalNumber) {
      // For childUser: only show serials assigned to them and not yet assigned in second
      return inventory
        .filter((invItem) => {
          if (invItem.itemDefinitionId !== itemDefinition.id) return false;
          if (!invItem.serialNumber || invItem.serialNumber.trim() === "")
            return false;

          // Must be assigned to childUser in assignments.first
          const firstAssignment =
            invItem.assignments?.first?.[childUserPersonalNumber];
          if (!firstAssignment) return false;

          // Must not be in assignments.second (not yet assigned by childUser)
          const secondAssignment =
            invItem.assignments?.second?.[childUserPersonalNumber];
          return !secondAssignment; // Available if not in second
        })
        .map((invItem) => invItem.serialNumber);
    }

    // For admin/parentsUser: show all available serials
    return inventory
      .filter(
        (invItem) =>
          invItem.itemDefinitionId === itemDefinition.id &&
          invItem.status === "available" &&
          invItem.serialNumber
      )
      .map((invItem) => invItem.serialNumber);
  }, [selectedItem, inventory, items, isChildUser, childUserPersonalNumber]);

  // Filter serials based on search term
  const filteredSerials = useMemo(() => {
    if (!serialSearchTerm.trim()) return availableSerials;

    return availableSerials.filter((serial) =>
      normalizeItemSearchText(serial).includes(
        normalizeItemSearchText(serialSearchTerm)
      )
    );
  }, [availableSerials, serialSearchTerm]);

  const selectedItemDefinition = useMemo(() => {
    if (!selectedItem) return null;
    let itemName = selectedItem;
    if (selectedItem.includes(" (") && selectedItem.includes(")")) {
      const match = selectedItem.match(/^(.+) \((אורגני|קווי)\)$/);
      if (match) itemName = match[1];
    }
    return items.find((i) => i.itemName === itemName) || null;
  }, [selectedItem, items]);

  const handleItemSelect = useCallback(
    (item) => {
      setSelectedItem(item);
      setItemSearchTerm(item);
      setShowItemOptions(false);
      setSelectedSerial(null);
      setSerialSearchTerm("");

      let itemName = item;
      let isOrganic = null;
      if (item.includes(" (") && item.includes(")")) {
        const match = item.match(/^(.+) \((אורגני|קווי)\)$/);
        if (match) {
          itemName = match[1];
          isOrganic = match[2] === "אורגני";
        }
      }
      const itemDef = items.find((i) => i.itemName === itemName);

      if (selectedEmployee && inventory?.length && itemDef) {
        const invItem = inventory.find(
          (inv) =>
            inv.itemDefinitionId === itemDef.id &&
            (isOrganic === null || inv.isOrganic === isOrganic),
        );
        if (invItem) {
          let existingNote = "";
          if (isChildUser && childUserPersonalNumber) {
            existingNote =
              invItem.assignments?.second?.[selectedEmployee.personalNumber]
                ?.note || "";
          } else {
            existingNote =
              invItem.assignments?.first?.[selectedEmployee.personalNumber]
                ?.note || "";
          }
          setNote(existingNote);
        }
      }
    },
    [selectedEmployee, inventory, items, isChildUser, childUserPersonalNumber],
  );

  const handleSerialSelect = useCallback((serial) => {
    setSelectedSerial(serial);
    setSerialSearchTerm(serial); // Show the selected serial in the input
    setShowSerialOptions(false);
  }, []);

  const checkDropdownPosition = useCallback((inputRef, setAbove) => {
    if (!inputRef.current) return;

    const rect = inputRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 300; // max-height from CSS

    // If there's not enough space below, position above
    const spaceBelow = viewportHeight - rect.bottom;
    const shouldPositionAbove = spaceBelow < dropdownHeight;

    setAbove(shouldPositionAbove);
  }, []);

  const handleItemInputFocus = useCallback(
    (e) => {
      setShowItemOptions(true);
      // Check position after a brief delay to ensure DOM is updated
      setTimeout(() => {
        checkDropdownPosition(e.target, setShowItemOptionsAbove);
      }, 10);
    },
    [checkDropdownPosition]
  );

  const handleItemInputBlur = useCallback(() => {
    setTimeout(() => setShowItemOptions(false), 200);
  }, []);

  const handleItemInputChange = useCallback((e) => {
    setItemSearchTerm(e.target.value);
    setShowItemOptions(true);
  }, []);

  const handleSerialInputFocus = useCallback(
    (e) => {
      setShowSerialOptions(true);
      // Check position after a brief delay to ensure DOM is updated
      setTimeout(() => {
        checkDropdownPosition(e.target, setShowSerialOptionsAbove);
      }, 10);
    },
    [checkDropdownPosition]
  );

  const handleSerialInputBlur = useCallback(() => {
    setTimeout(() => setShowSerialOptions(false), 200);
  }, []);

  const handleSerialInputChange = useCallback((e) => {
    setSerialSearchTerm(e.target.value);
    setShowSerialOptions(true);
  }, []);

  const handleQuantityChange = useCallback((e) => {
    setQuantity(parseInt(e.target.value) || 1);
  }, []);

  const handleQuantityBlur = useCallback(
    (e) => {
      const newQuantity = parseInt(e.target.value) || 1;

      // Get available quantity and adjust if needed
      if (selectedItem) {
        let itemName = selectedItem;
        let isOrganic = null;
        if (selectedItem.includes(" (") && selectedItem.includes(")")) {
          const match = selectedItem.match(/^(.+) \((אורגני|קווי)\)$/);
          if (match) {
            itemName = match[1];
            isOrganic = match[2] === "אורגני";
          }
        }

        const itemDefinition = items.find((item) => item.itemName === itemName);
        if (itemDefinition && !itemDefinition.isChecked) {
          const availableQuantity = getRemainingQuantity(
            itemDefinition.id,
            isOrganic
          );
          if (newQuantity > availableQuantity) {
            setQuantity(availableQuantity);
            return;
          }
        }
      }

      setQuantity(newQuantity);
    },
    [selectedItem, items, getRemainingQuantity]
  );

  const handleQuantityFocus = useCallback((e) => {
    // Select all text when focusing on quantity input
    e.target.select();
  }, []);

  const handleAddItem = useCallback(() => {
    if (!selectedItem) return;

    // Parse organic/synthetic from display name for non-serial items
    let itemName = selectedItem;
    let isOrganic = null;

    if (selectedItem.includes(" (") && selectedItem.includes(")")) {
      // Non-serial item: extract base name and organic flag
      const match = selectedItem.match(/^(.+) \((אורגני|קווי)\)$/);
      if (match) {
        itemName = match[1];
        isOrganic = match[2] === "אורגני";
      }
    }
    // For serial items, use the exact name as is

    const itemDefinition = items.find((item) => item.itemName === itemName);
    if (!itemDefinition) return;

    const hasSerialNumber = itemDefinition.isChecked;
    const serialNumber = hasSerialNumber ? selectedSerial : "";

    if (hasSerialNumber && !serialNumber) {
      alert("נא לבחור מספר סידורי");
      return;
    }

    if (hasSerialNumber) {
      const invExists = inventory.some(
        (inv) =>
          inv.itemDefinitionId === itemDefinition.id &&
          String(inv.serialNumber || "").trim() === String(serialNumber).trim(),
      );
      if (!invExists) {
        alert(
          "מספר סידורי זה לא נמצא במלאי הזמין. יש להוסיף אותו במסך המלאי לפני הוספה לעגלה.",
        );
        return;
      }
      // Check if serial number already in cart
      const existingCartItem = cart.find(
        (cartItem) => cartItem.serial === serialNumber
      );
      if (existingCartItem) {
        alert("מספר סידורי זה כבר נמצא בעגלה");
        return;
      }
    } else {
      // For non-serial items, quantity validation is handled in the input field
    }

    const cartItem = {
      id: hasSerialNumber
        ? inventory.find(
            (invItem) =>
              invItem.itemDefinitionId === itemDefinition.id &&
              invItem.serialNumber === serialNumber
          )?.id || Date.now().toString()
        : inventory.find(
            (invItem) =>
              invItem.itemDefinitionId === itemDefinition.id &&
              (invItem.availableQuantity ?? 0) > 0 &&
              (isOrganic === null || Boolean(invItem.isOrganic) === Boolean(isOrganic))
          )?.id || Date.now().toString(),
      item: selectedItem,
      serial: serialNumber,
      quantity: hasSerialNumber ? 1 : quantity,
      note: note,
      itemDefinitionId: itemDefinition.id,
      hasSerialNumber,
      isOrganic,
    };

    onAddItem(cartItem);

    // Reset form
    setSelectedItem(null);
    setItemSearchTerm("");
    setSelectedSerial(null);
    setSerialSearchTerm("");
    setQuantity(1);
    setNote("");
  }, [
    selectedItem,
    items,
    cart,
    quantity,
    note,
    selectedSerial,
    onAddItem,
    getRemainingQuantity,
    inventory,
  ]);

  const handleClose = useCallback(() => {
    onClose();
    setSelectedItem(null);
    setItemSearchTerm("");
    setSelectedSerial(null);
    setSerialSearchTerm("");
    setQuantity(1);
    setNote("");
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content signature-modal">
        <div className="modal-header">
          <h3>הוסף פריט לעגלה</h3>
          <button className="modal-close" onClick={handleClose}>
            <FaTimes />
          </button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>בחר פריט:</label>
            <div className="item-select-container">
              <input
                type="text"
                className="item-input"
                placeholder="חפש פריט..."
                value={itemSearchTerm}
                onChange={handleItemInputChange}
                onFocus={handleItemInputFocus}
                onBlur={handleItemInputBlur}
              />
              {showItemOptions && filteredItems.length > 0 && (
                <div
                  className={`item-options ${
                    showItemOptionsAbove ? "above" : ""
                  }`}
                >
                  {filteredItems.map((item, index) => (
                    <div
                      key={index}
                      className="item-option"
                      onClick={() => handleItemSelect(item.name)}
                    >
                      {item.name} (זמין: {item.availableQuantity})
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {selectedItem && (
            <>
              {(() => {
                // Extract base item name to check if it's a serial item
                let itemName = selectedItem;
                if (selectedItem.includes(" (") && selectedItem.includes(")")) {
                  const match = selectedItem.match(/^(.+) \((אורגני|קווי)\)$/);
                  if (match) {
                    itemName = match[1];
                  }
                }

                const itemDefinition = items.find(
                  (item) => item.itemName === itemName
                );
                return itemDefinition?.isChecked;
              })() && (
                <div className="form-group">
                  <label>מספר סידורי:</label>
                  <div className="item-select-container">
                    <input
                      type="text"
                      className="item-input"
                      placeholder="חפש מספר סידורי..."
                      value={serialSearchTerm}
                      onChange={handleSerialInputChange}
                      onFocus={handleSerialInputFocus}
                      onBlur={handleSerialInputBlur}
                    />
                    {showSerialOptions && (
                      <div
                        className={`item-options ${
                          showSerialOptionsAbove ? "above" : ""
                        }`}
                      >
                        {filteredSerials.map((serial, index) => (
                          <div
                            key={index}
                            className="item-option"
                            onClick={() => handleSerialSelect(serial)}
                          >
                            {serial}
                          </div>
                        ))}
                      </div>
                    )}
                    {serialSearchTerm.trim() &&
                      selectedItemDefinition?.isChecked &&
                      filteredSerials.length === 0 && (
                        <div className="signature-missing-serial-inline">
                          <p>
                            לא נמצאו מספרים תואמים במלאי הזמין לפריט זה (למשל
                            כבר משויך או לא קיים).
                          </p>
                          {typeof onAddStockShortcut === "function" && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                onAddStockShortcut({
                                  serialNumber: serialSearchTerm.trim(),
                                  itemDefinitionId:
                                    selectedItemDefinition?.id || "",
                                });
                              }}
                            >
                              הוסף למלאי (מקיצור דרך)
                            </button>
                          )}
                          {typeof onAddStockShortcut !== "function" && (
                            <p className="signature-missing-serial-child-hint">
                              פנה למנהל להוספת המספר במלאי.
                            </p>
                          )}
                        </div>
                      )}
                  </div>
                </div>
              )}

              {(() => {
                // Extract base item name to check if it's NOT a serial item
                let itemName = selectedItem;
                let isOrganic = null;
                if (selectedItem.includes(" (") && selectedItem.includes(")")) {
                  const match = selectedItem.match(/^(.+) \((אורגני|קווי)\)$/);
                  if (match) {
                    itemName = match[1];
                    isOrganic = match[2] === "אורגני";
                  }
                }

                const itemDefinition = items.find(
                  (item) => item.itemName === itemName
                );
                if (!itemDefinition || itemDefinition.isChecked) return false;

                const availableQuantity = getRemainingQuantity(
                  itemDefinition.id,
                  isOrganic
                );

                return (
                  <div className="form-group">
                    <label>כמות (זמין: {availableQuantity}):</label>
                    <input
                      type="number"
                      min="1"
                      max={availableQuantity}
                      value={quantity}
                      onChange={handleQuantityChange}
                      onFocus={handleQuantityFocus}
                      onBlur={handleQuantityBlur}
                      className="form-input"
                    />
                  </div>
                );
              })()}

              <div className="form-group">
                <label>ציין מיקום הפריט או הערה (לא חובה):</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="form-input"
                  rows="3"
                  placeholder="ציין מיקום הפריט או הערה"
                />
              </div>
            </>
          )}

          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={handleClose}>
              ביטול
            </button>
            <button
              className="btn btn-primary"
              onClick={handleAddItem}
              disabled={!selectedItem}
            >
              הוסף לעגלה
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(AddItemModal);
